import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  MenuItem,
  Stack,
  Alert,
  InputAdornment,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedIcon from '@mui/icons-material/Verified';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import apiClient, { getErrorMessage } from '../../utils/axios';
import { formatKstDateTime, formatRelative, formatNumber } from '../../utils/formatUtils';
import { PageHeader, UserStatusChip, SubscriptionChip, USER_STATUS_OPTIONS } from '../../components/common';

const Users = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(params.get('search') || '');
  const [data, setData] = useState({ items: [], total: 0 });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const query = {
    search: params.get('search') || '',
    status: params.get('status') || '',
    subscription: params.get('subscription') || '',
    verified: params.get('verified') || '',
    sort: params.get('sort') || 'id',
    page: Number(params.get('page') || 1),
    pageSize: Number(params.get('pageSize') || 50),
  };
  const queryKey = params.toString();

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v === '' || v == null ? next.delete(k) : next.set(k, String(v))));
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/users', { params: Object.fromEntries(new URLSearchParams(queryKey)) });
      setData(res.data);
      setError('');
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [queryKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== query.search) update({ search: searchInput.trim() });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  return (
    <>
      <PageHeader title="회원 관리" description={`검색 결과 ${formatNumber(data.total)}명 · 행을 누르면 상세 화면으로 이동합니다.`} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            size="small"
            placeholder="이메일 · 이름 · 휴대폰 · 회원ID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField select size="small" label="회원 상태" value={query.status} onChange={(e) => update({ status: e.target.value })} sx={{ minWidth: 130 }}>
            <MenuItem value="">전체</MenuItem>
            {USER_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="구독" value={query.subscription} onChange={(e) => update({ subscription: e.target.value })} sx={{ minWidth: 130 }}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="active">구독중</MenuItem>
            <MenuItem value="none">무료</MenuItem>
            <MenuItem value="expired">만료</MenuItem>
            <MenuItem value="cancelled">취소</MenuItem>
          </TextField>
          <TextField select size="small" label="이메일 인증" value={query.verified} onChange={(e) => update({ verified: e.target.value })} sx={{ minWidth: 130 }}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="Y">인증</MenuItem>
            <MenuItem value="N">미인증</MenuItem>
          </TextField>
          <TextField select size="small" label="정렬" value={query.sort} onChange={(e) => update({ sort: e.target.value })} sx={{ minWidth: 130 }}>
            <MenuItem value="id">최근 가입(ID)</MenuItem>
            <MenuItem value="lastLogin">최근 로그인</MenuItem>
            <MenuItem value="created">가입일</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <Paper>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>이름 / 이메일</TableCell>
              <TableCell>휴대폰</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>구독</TableCell>
              <TableCell align="center">에이전트</TableCell>
              <TableCell>최근 로그인</TableCell>
              <TableCell>가입일</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  회원이 없습니다.
                </TableCell>
              </TableRow>
            )}
            {data.items.map((u) => (
              <TableRow key={u.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/users/${encodeURIComponent(u.id)}`)}>
                <TableCell>{u.id}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.username || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {u.email}
                    {u.emailVerifiedAt ? (
                      <Tooltip title="이메일 인증됨"><VerifiedIcon sx={{ fontSize: 14 }} color="success" /></Tooltip>
                    ) : (
                      <Tooltip title="이메일 미인증"><ErrorOutlineIcon sx={{ fontSize: 14 }} color="warning" /></Tooltip>
                    )}
                  </Typography>
                </TableCell>
                <TableCell>{u.phoneNumber || '-'}</TableCell>
                <TableCell><UserStatusChip status={u.status} /></TableCell>
                <TableCell>
                  <SubscriptionChip status={u.subscriptionStatus} planName={u.subscriptionPlanName} expiresAt={u.subscriptionExpiresAt} />
                </TableCell>
                <TableCell align="center">{u.agentCount || '-'}</TableCell>
                <TableCell>
                  <Tooltip title={formatKstDateTime(u.lastLoginAt)}>
                    <span>{formatRelative(u.lastLoginAt)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{formatKstDateTime(u.createdAt, { seconds: false })}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data.total}
          page={query.page - 1}
          rowsPerPage={query.pageSize}
          rowsPerPageOptions={[20, 50, 100, 200]}
          onPageChange={(_e, p) => update({ page: p + 1 })}
          onRowsPerPageChange={(e) => update({ pageSize: e.target.value })}
          labelRowsPerPage="페이지당"
        />
      </Paper>
    </>
  );
};

export default Users;
